import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  ensureJobChatChannel,
  fetchChatMessages,
  sendChatMessage,
  uploadChatAttachment,
  markChannelRead,
  chatSenderLabel,
  type ChatMessage,
} from '../../api/teamChat'
import { ChatComposeBar } from '../chat/ChatComposeBar'
import { ChatMessageBody } from '../chat/ChatMessageBody'

interface JobChatPanelProps {
  jobId: string
  /** Taller message list for dedicated mobile chat pages. */
  tall?: boolean
}

export function JobChatPanel({ jobId, tall }: JobChatPanelProps) {
  const [channelId, setChannelId] = useState<string | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [body, setBody] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const loadMessages = useCallback(async (cid: string) => {
    const rows = await fetchChatMessages(cid)
    setMessages(rows)
    await markChannelRead(cid)
  }, [])

  const init = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const cid = await ensureJobChatChannel(jobId)
      setChannelId(cid)
      await loadMessages(cid)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load job chat')
    } finally {
      setLoading(false)
    }
  }, [jobId, loadMessages])

  useEffect(() => {
    init()
  }, [init])

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight })
  }, [messages])

  const handleSend = async (file?: File | null, mentionedUserIds?: string[]) => {
    if (!channelId || sending) return
    if (!body.trim() && !file) return
    setSending(true)
    setError(null)
    try {
      let attachmentUrl: string | null = null
      if (file) {
        attachmentUrl = await uploadChatAttachment(channelId, file)
      }
      await sendChatMessage(channelId, body, attachmentUrl, mentionedUserIds)
      setBody('')
      await loadMessages(channelId)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send')
    } finally {
      setSending(false)
    }
  }

  if (loading) {
    return <p className="text-sm text-gray-500">Loading team chat…</p>
  }

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-gray-900">Job team chat</h3>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div
        ref={listRef}
        className={`space-y-2 overflow-y-auto ${tall ? 'max-h-[min(60dvh,28rem)]' : 'max-h-64'}`}
      >
        {messages.length === 0 ? (
          <p className="text-sm text-gray-500">No messages yet.</p>
        ) : (
          messages.map((m) => (
            <div key={m.id} className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs font-medium text-gray-700 mb-1">{chatSenderLabel(m)}</p>
              <ChatMessageBody body={m.body} attachmentUrl={m.attachment_url} />
              <p className="text-xs text-gray-500 mt-1">{new Date(m.created_at).toLocaleString()}</p>
            </div>
          ))
        )}
      </div>
      <ChatComposeBar
        value={body}
        onChange={setBody}
        onSend={handleSend}
        disabled={!channelId}
        sending={sending}
        placeholder="Message the job channel… Use @ to ping someone"
        compact
      />
    </div>
  )
}
